import { useQuery } from '@apollo/client';
import { channel } from 'diagnostics_channel';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useSWR from 'swr';
import { getHTMLFile } from '../../../../api/transcripts';
import GraphQLErrorText from '../../../../components/GraphQLErrorText';
import Loading from '../../../../components/Loading';
import Channel from '../../../../components/svgs/Channel';
import useAuthOrRedirect from '../../../../hooks/useAuthOrRedirect';
import { PROFILE } from '../../../../lib/graphql/queries';
import { WHITE_LABEL_LOGO } from '../../../../lib/white-label';
import { analytics } from '../../../../lib/analytics';

const TranscriptsWrapper = styled.div`
  height: calc(100vh - 80px);
  display: grid;
  grid-template-columns: 250px 1fr;
  @media only screen and (max-width: 700px) {
    grid-template-columns: 150px 1fr;
  }
`;

const TranscriptsChannelsScrollView = styled.div`
  overflow: auto;
  background-color: #f9faff;
  border: 1px solid ${(props) => props.theme.colors.borderColors[0]};
  height: 100%;
  padding: 16px;
  border-radius: 4px;
`;

const TranscriptsChannelView = styled.div`
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  background-color: #f9faff;
  border-radius: 4px;
  border: 1px solid ${(props) => props.theme.colors.borderColors[0]};
  .chatlog-divider {
    background-color: ${(props) => props.theme.colors.borderColors[0]};
  }
  .chatlog__author-name {
    color: #000 !important;
  }
  .preamble__entry {
    color: #000 !important;
  }
  .chatlog__message {
    color: #000 !important;
  }
`;

const TranscriptsCategoryCard = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  align-items: center;
  grid-gap: 0 8px;
  cursor: pointer !important;
  color: #000;
  border-radius: 4px;
  padding: 0px 8px;
  width: 100%;
  margin-bottom: 16px;
  & > div > p {
    margin-bottom: 4px;
    font-size: 16px !important;
    font-weight: 500;
  }
`;

const TranscriptsChannelCard = styled.div<{
  isActive: boolean;
}>`
  display: grid;
  grid-template-columns: 16px 1fr;
  align-items: center;
  margin-left: 8px;
  grid-gap: 0 8px;
  background-color: ${(props) => (props.isActive ? '#E5E8F0 !important' : '')};
  cursor: pointer !important;
  color: #000;
  border-radius: 4px;
  padding: 0px 8px;
  width: 100%;
  margin-bottom: 2px;
  &:hover {
    background-color: #e5e8f0 !important;
  }
  div > p {
    font-size: 14px !important;
  }
`;

export const TranscriptsEmptyView = styled.div`
  text-align: center;
  height: 70vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  word-break: break-word;
  max-width: 800px;
  margin: 0 auto;
  h1 {
    font-size: 2rem;
    font-weight: 700;
  }
`;

const Transcripts = (): JSX.Element => {
  const router = useRouter();

  const { loading, error } = useQuery(PROFILE);
  const [currentChannel, setCurrentChannel] = useState(null);
  const channelViewRef = useRef<any>();

  const {
    data: channels,
    error: err,
    isValidating,
    mutate,
  } = useSWR(`/discord-transcripts-mirror/channels?botId=${router.query.id}`, {
    revalidateOnFocus: false,
  });

  const {
    data: channelData,
    error: channelErr,
    isValidating: isValidatingChannel,
  } = useSWR(
    currentChannel !== null
      ? `/discord-transcripts-mirror/transcript?channelId=${currentChannel}`
      : null,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: (error) => {
        // We skip retrying if the API is returning 404:
        if (error?.response?.status === 404) return false;
        return true;
      },
    },
  );

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const handleScrollToBottom = async () => {
    await sleep(100);
    const channelView = document.getElementById('channelView');
    if (channelView)
      channelView.scroll({ behavior: 'smooth', top: channelView.scrollHeight });
  };

  useEffect(() => {
    if (channels && channels.length > 0) {
      for (let i = 0; i < channels.length; i++) {
        for (let j = 0; j < channels[i].children?.length; j++) {
          router.push(
            `/bots/guides/bot/${router.query.id}?channel=${channels[i].children[j].id}`,
          );
          setCurrentChannel(channels[i].children[j].id);
          break;
        }
      }
    }
  }, [channels]);

  useEffect(() => {
    handleScrollToBottom();
  }, [channelData]);

  useEffect(() => {
    analytics.page(`Rental Guides - ${router.query.id}`);
  }, [router.query.id]);

  if (!useAuthOrRedirect()) return null;
  if (loading || isValidating || (!channels && !err)) return <Loading />;
  if (error) return <GraphQLErrorText error={error} />;
  if (err)
    return (
      <TranscriptsEmptyView>
        <Image alt="" width={200} height={200} src={WHITE_LABEL_LOGO} />
        <h1>{err.response.data.message}</h1>
      </TranscriptsEmptyView>
    );

  return channels?.length == 0 ? (
    <TranscriptsEmptyView>
      <Image alt="" width={200} height={200} src={WHITE_LABEL_LOGO} />
      <h1>No channels found for the selected bot!</h1>
    </TranscriptsEmptyView>
  ) : (
    <TranscriptsWrapper key={`${currentChannel}`}>
      <TranscriptsChannelsScrollView>
        {channels?.map(
          (category, key) =>
            category.isCategory && (
              <div key={key}>
                <TranscriptsCategoryCard>
                  <div>
                    <p>{category.name}</p>
                    {category.children?.map((channel, channelKey) => {
                      return (
                        <TranscriptsChannelCard
                          key={channelKey}
                          isActive={currentChannel == channel.id}
                          onClick={() => {
                            router.push(
                              `/bots/guides/bot/${router.query.id}?channel=${channel.id}`,
                            );
                            setCurrentChannel(channel.id);
                          }}
                        >
                          <Channel />
                          <p>{channel.name}</p>
                        </TranscriptsChannelCard>
                      );
                    })}
                  </div>
                </TranscriptsCategoryCard>
              </div>
            ),
        )}
      </TranscriptsChannelsScrollView>
      <TranscriptsChannelView id="channelView" ref={channelViewRef}>
        {isValidatingChannel && !channelData ? (
          <Loading />
        ) : channelErr && channelErr?.response.status === 404 ? (
          <TranscriptsEmptyView>
            <Image alt="" width={200} height={200} src={WHITE_LABEL_LOGO} />
            <h1>Channel data not found</h1>
          </TranscriptsEmptyView>
        ) : (
          <div dangerouslySetInnerHTML={{ __html: channelData }} />
        )}
      </TranscriptsChannelView>
    </TranscriptsWrapper>
  );
};

export default Transcripts;
